/**
 * Snackbar <uo-snackbar>
 * @element uo-snackbar
 */
var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __classPrivateFieldGet = (this && this.__classPrivateFieldGet) || function (receiver, state, kind, f) {
    if (kind === "a" && !f) throw new TypeError("Private accessor was defined without a getter");
    if (typeof state === "function" ? receiver !== state || !f : !state.has(receiver)) throw new TypeError("Cannot read private member from an object whose class did not declare it");
    return kind === "m" ? f : kind === "a" ? f.call(receiver) : f ? f.value : state.get(receiver);
};
var __classPrivateFieldSet = (this && this.__classPrivateFieldSet) || function (receiver, state, value, kind, f) {
    if (kind === "m") throw new TypeError("Private method is not writable");
    if (kind === "a" && !f) throw new TypeError("Private accessor was defined without a setter");
    if (typeof state === "function" ? receiver !== state || !f : !state.has(receiver)) throw new TypeError("Cannot write private member to an object whose class did not declare it");
    return (kind === "a" ? f.call(receiver, value) : f ? f.value = value : state.set(receiver, value)), value;
};
var _Snackbar_message_accessor_storage, _Snackbar_isFixed_accessor_storage, _Snackbar_position_accessor_storage, _Snackbar_color_accessor_storage, _Snackbar_duration_accessor_storage;
// Import variables
import './variables.scss';
// Import dependencies
import { unsafeCSS, css, html, LitElement } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { ifDefined } from 'lit/directives/if-defined.js';
// Import global utils
import { isEmpty } from '@automator/shared/utils/is-empty';
// Import styles
import styles from './web-component-style.scss?inline';
/**
 * @element uo-snackbar
 * @summary A snackbar/toast notification component that can display messages with optional icons and actions.
 *
 * @slot icon - Slot for an icon to be displayed at the start of the snackbar.
 * @slot action - Slot for an action button or link to be displayed at the end of the snackbar.
 *
 * @attr {string} [message=''] - The primary message text to display in the snackbar. Reflects `message` property.
 * @attr {boolean} [is-fixed=false] - If true, positions the snackbar using `position: fixed` relative to the viewport. Otherwise, uses `position: absolute`. Reflects `isFixed` property.
 * @attr {'bottom-center' | 'bottom-left' | 'bottom-right' | 'top-center' | 'top-left' | 'top-right'} [position='bottom-center'] - Determines the position of the snackbar on the screen. Reflects `position` property.
 * @attr {'' | 'error'} [color=''] - Sets the color scheme of the snackbar. An empty string implies the default color. Reflects `color` property.
 * @attr {number} [duration=5000] - The duration in milliseconds for the snackbar to be visible. A value of -1 makes it persist indefinitely until manually removed. Reflects `duration` property.
 *
 * @cssprop [--uap-snackbar-min-width=auto] - Minimum width of the snackbar.
 * @cssprop [--uap-snackbar-max-width=auto] - Maximum width of the snackbar.
 * @cssprop [--uap-snackbar-font-size=var(--uap-font-size--body-regular)] - Font size for the snackbar message.
 * @cssprop [--uap-snackbar-background=var(--uap-color-grey-dark)] - Default background color of the snackbar. (Note: SCSS uses a direct value, but this is a common pattern for theming)
 * @cssprop [--uap-snackbar-background-error=var(--uap-color-red)] - Background color for the 'error' snackbar variant. (Note: SCSS uses a direct value)
 * @cssprop [--uap-snackbar-box-shadow=var(--uap-box-shadow-medium)] - Box shadow for the snackbar.
 * @cssprop [--uap-border-radius=var(--uap-border-radius)] - Border radius for the snackbar. (Used via a shared variable)
 * @cssprop [--uap-snackbar-padding=15px] - Horizontal padding for the snackbar content areas (icon, message). Vertical padding for icon/message is also derived from this.
 * @cssprop [--uap-snackbar-margin-from-border=20px] - Margin from the screen edges when positioned (e.g., bottom, left, right, top). (Note: SCSS uses direct values, but this is a common pattern)
 */
let Snackbar = class Snackbar extends LitElement {
    constructor() {
        super(...arguments);
        _Snackbar_message_accessor_storage.set(this, '');
        _Snackbar_isFixed_accessor_storage.set(this, false);
        _Snackbar_position_accessor_storage.set(this, 'bottom-center');
        _Snackbar_color_accessor_storage.set(this, '');
        _Snackbar_duration_accessor_storage.set(this, 5000);
    }
    /**
     * The primary message text to display in the snackbar.
     * @attr message
     * @default ''
     */
    get message() { return __classPrivateFieldGet(this, _Snackbar_message_accessor_storage, "f"); }
    set message(value) { __classPrivateFieldSet(this, _Snackbar_message_accessor_storage, value, "f"); }
    /**
     * If true, positions the snackbar using `position: fixed`. Otherwise, `position: absolute`.
     * Reflects to the `is-fixed` attribute.
     * @attr is-fixed
     * @default false
     */
    get isFixed() { return __classPrivateFieldGet(this, _Snackbar_isFixed_accessor_storage, "f"); }
    set isFixed(value) { __classPrivateFieldSet(this, _Snackbar_isFixed_accessor_storage, value, "f"); }
    /**
     * Determines the position of the snackbar on the screen.
     * @attr position
     * @default 'bottom-center'
     */
    get position() { return __classPrivateFieldGet(this, _Snackbar_position_accessor_storage, "f"); }
    set position(value) { __classPrivateFieldSet(this, _Snackbar_position_accessor_storage, value, "f"); }
    /**
     * Sets the color scheme of the snackbar.
     * An empty string implies the default color.
     * @attr color
     * @default ''
     */
    get color() { return __classPrivateFieldGet(this, _Snackbar_color_accessor_storage, "f"); }
    set color(value) { __classPrivateFieldSet(this, _Snackbar_color_accessor_storage, value, "f"); }
    /**
     * The duration in milliseconds for the snackbar to be visible.
     * A value of -1 makes it persist indefinitely.
     * @attr duration
     * @default 5000
     */
    get duration() { return __classPrivateFieldGet(this, _Snackbar_duration_accessor_storage, "f"); }
    set duration(value) { __classPrivateFieldSet(this, _Snackbar_duration_accessor_storage, value, "f"); }
    /**
     * Renders the component's DOM structure.
     * @return {TemplateResult} The HTML template for the snackbar.
     */
    render() {
        return html `
			<div
				class="snackbar"
				position=${this.position}
				color=${ifDefined(this.color || undefined)}
				?is-fixed=${this.isFixed}
			>
				<div class="snackbar-icon">
					<slot name="icon"></slot>
				</div>

				<div class="snackbar-message">
					${this.message}
				</div>

				<div class="snackbar-action">
					<slot name="action"></slot>
				</div>

				${this._templateProgress}
			</div>
		`;
    }
    /**
     * Generates the template for the progress bar if the duration is finite.
     * @return {TemplateResult | undefined} The HTML template for the progress bar, or undefined if duration is -1.
     * @protected
     */
    get _templateProgress() {
        if (this.duration === -1) {
            return;
        }
        return html `
			<div
				class="snackbar-progress"
				style="animation-duration: ${this.duration}ms"
			></div>
		`;
    }
    /**
     * Lifecycle callback invoked after the component's first update.
     * Sets up a timer to automatically remove the snackbar if a finite duration is specified.
     */
    firstUpdated() {
        if (isEmpty(this.duration) || this.duration === -1) {
            return;
        }
        setTimeout(() => {
            // Remove the snackbar element from the DOM
            this.remove();
        }, this.duration);
    }
};
_Snackbar_message_accessor_storage = new WeakMap();
_Snackbar_isFixed_accessor_storage = new WeakMap();
_Snackbar_position_accessor_storage = new WeakMap();
_Snackbar_color_accessor_storage = new WeakMap();
_Snackbar_duration_accessor_storage = new WeakMap();
/**
 * Defines the component's CSS styles.
 * `unsafeCSS` is used because the styles are imported as a string.
 */
Snackbar.styles = css `${unsafeCSS(styles)}`;
__decorate([
    property({ type: String })
], Snackbar.prototype, "message", null);
__decorate([
    property({ type: Boolean, attribute: 'is-fixed' })
], Snackbar.prototype, "isFixed", null);
__decorate([
    property({ type: String })
], Snackbar.prototype, "position", null);
__decorate([
    property({ type: String })
], Snackbar.prototype, "color", null);
__decorate([
    property({ type: Number })
], Snackbar.prototype, "duration", null);
Snackbar = __decorate([
    customElement('uo-snackbar')
], Snackbar);
export { Snackbar };
